<template>
  <el-dialog
    :model-value="modelValue"
    class="customer-profile-dialog"
    width="980px"
    top="3vh"
    append-to-body
    :close-on-click-modal="false"
    :show-close="true"
    @close="handleClose"
  >
    <div class="dialog-shell">
      <aside class="dialog-hero">
        <p class="hero-eyebrow">Customer Portal</p>
        <h2>Complete your research support profile</h2>
        <p class="hero-copy">
          This profile helps BGIR match the right services, timeline, and communication preferences before you create a submission.
        </p>

        <div class="progress-card">
          <span class="progress-label">Progress</span>
          <strong>{{ activeStep }} / {{ steps.length }}</strong>
          <el-progress
            :percentage="Math.round((activeStep / steps.length) * 100)"
            :stroke-width="10"
            :show-text="false"
            color="#0b6cff"
          />
        </div>
      </aside>

      <section class="dialog-main">
        <el-form label-position="top" class="profile-form" @submit.prevent>
          <section v-show="activeStep === 1" class="step-panel">
            <div class="section-card">
              <div class="grid two-up">
                <el-form-item label="1. First Name" :error="errors.firstName">
                  <el-input v-model="form.firstName" maxlength="80" />
                </el-form-item>
                <el-form-item label="2. Last Name" :error="errors.lastName">
                  <el-input v-model="form.lastName" maxlength="80" />
                </el-form-item>
              </div>

              <div class="grid two-up">
                <el-form-item label="3. Email Address" :error="errors.email">
                  <el-input v-model="form.email" maxlength="160" />
                </el-form-item>
                <el-form-item label="4. Primary Contact Number" :error="errors.primaryContactNumber">
                  <el-input v-model="form.primaryContactNumber" maxlength="30" />
                </el-form-item>
              </div>

              <div class="grid two-up">
                <el-form-item label="5. Are you an international student?" :error="errors.isInternationalStudent">
                  <el-radio-group v-model="form.isInternationalStudent" class="option-group">
                    <el-radio label="yes">Yes</el-radio>
                    <el-radio label="no">No</el-radio>
                  </el-radio-group>
                </el-form-item>
                <el-form-item label="6. Country of citizenship" :error="errors.countryOfCitizenship">
                  <el-input v-model="form.countryOfCitizenship" maxlength="100" />
                </el-form-item>
              </div>
            </div>
          </section>

          <section v-show="activeStep === 2" class="step-panel">
            <div class="section-card">
              <el-form-item
                label="7. Which of the following best describes your current status?"
                :error="errors.currentStatus"
              >
                <el-radio-group v-model="form.currentStatus" class="option-group option-grid">
                  <el-radio label="student">Student</el-radio>
                  <el-radio label="employed">Employed</el-radio>
                  <el-radio label="unemployed">Unemployed</el-radio>
                  <el-radio label="retired">Retired</el-radio>
                  <el-radio label="prefer_not_to_say">Prefer not to say</el-radio>
                  <el-radio label="other">Other</el-radio>
                </el-radio-group>
              </el-form-item>

              <el-form-item
                v-if="form.currentStatus === 'other'"
                label="Current status details"
                :error="errors.currentStatusOther"
              >
                <el-input v-model="form.currentStatusOther" maxlength="120" />
              </el-form-item>

              <el-form-item
                label="8. What is the highest level of education that you have completed?"
                :error="errors.highestEducation"
              >
                <el-radio-group v-model="form.highestEducation" class="option-group option-grid">
                  <el-radio label="elementary">Elementary</el-radio>
                  <el-radio label="middle_school">Middle school</el-radio>
                  <el-radio label="high_school">High school</el-radio>
                  <el-radio label="college_certificate">College certificate</el-radio>
                  <el-radio label="university_degree">University degree</el-radio>
                  <el-radio label="undeclared">Undeclared</el-radio>
                  <el-radio label="prefer_not_to_say">Prefer not to say</el-radio>
                  <el-radio label="other">Other</el-radio>
                </el-radio-group>
              </el-form-item>

              <el-form-item
                v-if="form.highestEducation === 'other'"
                label="Education level details"
                :error="errors.highestEducationOther"
              >
                <el-input v-model="form.highestEducationOther" maxlength="120" />
              </el-form-item>
            </div>
          </section>

          <section v-show="activeStep === 3" class="step-panel">
            <div class="section-card">
              <el-form-item
                label="9. Please select the service(s) you are requesting"
                :error="errors.requestedServices"
              >
                <el-checkbox-group v-model="form.requestedServices" class="services-grid">
                  <el-checkbox
                    v-for="option in serviceOptions"
                    :key="option"
                    :label="option"
                    class="service-option"
                  >
                    {{ option }}
                  </el-checkbox>
                </el-checkbox-group>
              </el-form-item>
            </div>
          </section>

          <section v-show="activeStep === 4" class="step-panel">
            <div class="section-card">
              <el-form-item
                label="10. Select your preferred course time and day"
                :error="errors.preferredSchedule"
              >
                <el-radio-group v-model="form.preferredSchedule" class="option-group">
                  <el-radio label="monday_to_friday_morning">Monday to Friday - Morning</el-radio>
                  <el-radio label="monday_to_friday_afternoon">Monday to Friday - Afternoon</el-radio>
                  <el-radio label="monday_to_friday_evening">Monday to Friday - Evening</el-radio>
                </el-radio-group>
              </el-form-item>

              <el-form-item
                label="11. To ensure we provide the best possible service, please share any special requirements or preferences you may have"
                :error="errors.specialRequirementsOrPreferences"
              >
                <el-input
                  v-model="form.specialRequirementsOrPreferences"
                  type="textarea"
                  :rows="6"
                  maxlength="2000"
                  show-word-limit
                />
              </el-form-item>

              <el-form-item
                label="12. Please select the time frame for the service"
                :error="errors.serviceTimeFrame"
              >
                <el-radio-group v-model="form.serviceTimeFrame" class="option-group">
                  <el-radio label="standard_service">Standard service (2-6 weeks)</el-radio>
                  <el-radio label="expedited_service">Expedited service (1-2 weeks)</el-radio>
                </el-radio-group>
              </el-form-item>
            </div>
          </section>

          <section v-show="activeStep === 5" class="step-panel">
            <div class="section-card">
              <div class="disclaimer-box">
                <p class="disclaimer-title">BGIR Research Support &amp; Academic Services Disclaimer</p>
                <p>
                  By submitting this request, you hereby acknowledge and attest to the following conditions, which govern the processing,
                  review, and potential approval of BGIR research support services:
                </p>
                <ul class="disclaimer-list">
                  <li>
                    The information provided in this submission will be utilized solely for review, resource allocation, and administrative
                    processing in accordance with applicable BGIR research compliance, and institutional oversight policies.
                  </li>
                  <li>
                    Submission of this form constitutes an initial request for services and does not imply, guarantee, or confer approval,
                    prioritization, or initiation of any research-related activity.
                  </li>
                  <li>
                    Certain services may be delivered through virtual, hybrid, or alternative modalities as determined appropriate by the BGIR team.
                  </li>
                  <li>
                    Requests that involve incomplete information or raise regulatory, ethical, or compliance considerations may be assigned a pending status.
                  </li>
                  <li>
                    You retain the right to withdraw your request at any point prior to the issuance of an official service confirmation.
                  </li>
                  <li>
                    Contact information and relevant submission details may be shared with designated institutional offices or oversight bodies when required.
                  </li>
                </ul>
              </div>

              <el-form-item
                label="13. I've read the above information and would like to continue my registration"
                :error="errors.registrationDecision"
              >
                <el-radio-group v-model="form.registrationDecision" class="option-group">
                  <el-radio label="agree">Agree, please submit my registration.</el-radio>
                  <el-radio label="disagree">Disagree, please dismiss my registration.</el-radio>
                </el-radio-group>
              </el-form-item>
            </div>
          </section>
        </el-form>

        <div class="dialog-actions">
          <el-button v-if="activeStep > 1" @click="previousStep">
            Back
          </el-button>
          <el-button @click="handleClose">
            Close
          </el-button>
          <el-button
            v-if="activeStep < steps.length"
            type="primary"
            @click="nextStep"
          >
            Continue
          </el-button>
          <el-button
            v-else
            type="primary"
            :loading="saving"
            @click="submitProfile"
          >
            Save profile
          </el-button>
        </div>
      </section>
    </div>
  </el-dialog>
</template>

<script>
import {
  CUSTOMER_PROFILE_REQUIRED_SERVICE_OPTIONS,
  buildCustomerProfileDraft,
} from '@/services/userProfile'
import { ElMessage } from 'element-plus'

const STEPS = Object.freeze([
  { id: 1, title: 'Personal details' },
  { id: 2, title: 'Status and education' },
  { id: 3, title: 'Requested services' },
  { id: 4, title: 'Preferences' },
  { id: 5, title: 'Confirmation' },
])

function createEmptyErrors() {
  return {
    firstName: '',
    lastName: '',
    email: '',
    primaryContactNumber: '',
    isInternationalStudent: '',
    countryOfCitizenship: '',
    currentStatus: '',
    currentStatusOther: '',
    highestEducation: '',
    highestEducationOther: '',
    requestedServices: '',
    preferredSchedule: '',
    specialRequirementsOrPreferences: '',
    serviceTimeFrame: '',
    registrationDecision: '',
  }
}

export default {
  name: 'CustomerProfileDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    initialProfile: { type: Object, default: () => ({}) },
    currentUser: { type: Object, default: null },
    saving: { type: Boolean, default: false },
  },
  emits: ['close', 'save'],
  data() {
    return {
      activeStep: 1,
      form: buildCustomerProfileDraft(this.currentUser, this.initialProfile),
      errors: createEmptyErrors(),
      serviceOptions: CUSTOMER_PROFILE_REQUIRED_SERVICE_OPTIONS,
      steps: STEPS,
    }
  },
  watch: {
    initialProfile: {
      immediate: true,
      handler(profile) {
        this.form = buildCustomerProfileDraft(this.currentUser, profile)
      }
    },
    currentUser(user) {
      this.form = buildCustomerProfileDraft(user, this.initialProfile)
    },
    modelValue(isOpen) {
      if (isOpen) {
        this.activeStep = 1
        this.errors = createEmptyErrors()
      }
    }
  },
  methods: {
    handleClose() {
      this.$emit('close')
    },
    clearErrors(fields = []) {
      if (!fields.length) {
        this.errors = createEmptyErrors()
        return
      }
      fields.forEach((field) => {
        this.errors[field] = ''
      })
    },
    validateStep(step) {
      if (step === 1) {
        this.clearErrors([
          'firstName',
          'lastName',
          'email',
          'primaryContactNumber',
          'isInternationalStudent',
          'countryOfCitizenship',
        ])

        if (!this.form.firstName?.trim()) this.errors.firstName = 'First name is required.'
        if (!this.form.lastName?.trim()) this.errors.lastName = 'Last name is required.'
        if (!this.form.email?.trim()) this.errors.email = 'Email address is required.'
        if (!this.form.primaryContactNumber?.trim()) this.errors.primaryContactNumber = 'Primary contact number is required.'
        if (!['yes', 'no'].includes(this.form.isInternationalStudent)) this.errors.isInternationalStudent = 'Please select yes or no.'
        if (!this.form.countryOfCitizenship?.trim()) this.errors.countryOfCitizenship = 'Country of citizenship is required.'

        return !this.errors.firstName
          && !this.errors.lastName
          && !this.errors.email
          && !this.errors.primaryContactNumber
          && !this.errors.isInternationalStudent
          && !this.errors.countryOfCitizenship
      }

      if (step === 2) {
        this.clearErrors([
          'currentStatus',
          'currentStatusOther',
          'highestEducation',
          'highestEducationOther',
        ])

        if (!this.form.currentStatus) this.errors.currentStatus = 'Please select your current status.'
        if (this.form.currentStatus === 'other' && !this.form.currentStatusOther?.trim()) {
          this.errors.currentStatusOther = 'Please describe your current status.'
        }
        if (!this.form.highestEducation) this.errors.highestEducation = 'Please select the highest level of education completed.'
        if (this.form.highestEducation === 'other' && !this.form.highestEducationOther?.trim()) {
          this.errors.highestEducationOther = 'Please describe your education level.'
        }

        return !this.errors.currentStatus
          && !this.errors.currentStatusOther
          && !this.errors.highestEducation
          && !this.errors.highestEducationOther
      }

      if (step === 3) {
        this.clearErrors(['requestedServices'])
        if (!Array.isArray(this.form.requestedServices) || this.form.requestedServices.length === 0) {
          this.errors.requestedServices = 'Select at least one requested service.'
        }
        return !this.errors.requestedServices
      }

      if (step === 4) {
        this.clearErrors([
          'preferredSchedule',
          'specialRequirementsOrPreferences',
          'serviceTimeFrame',
        ])

        if (!this.form.preferredSchedule) this.errors.preferredSchedule = 'Please select your preferred course time and day.'
        if (!this.form.specialRequirementsOrPreferences?.trim()) {
          this.errors.specialRequirementsOrPreferences = 'Please share your requirements or preferences.'
        }
        if (!this.form.serviceTimeFrame) this.errors.serviceTimeFrame = 'Please select the service time frame.'

        return !this.errors.preferredSchedule
          && !this.errors.specialRequirementsOrPreferences
          && !this.errors.serviceTimeFrame
      }

      this.clearErrors(['registrationDecision'])

      if (!this.form.registrationDecision) {
        this.errors.registrationDecision = 'Please choose whether to continue your registration.'
      } else if (this.form.registrationDecision !== 'agree') {
        this.errors.registrationDecision = 'You must agree to continue your registration.'
      }

      return !this.errors.registrationDecision
    },
    nextStep() {
      if (!this.validateStep(this.activeStep)) return
      this.activeStep += 1
    },
    previousStep() {
      if (this.activeStep > 1) this.activeStep -= 1
    },
    submitProfile() {
      for (const step of this.steps) {
        if (!this.validateStep(step.id)) {
          this.activeStep = step.id
          if (step.id === 5 && this.form.registrationDecision === 'disagree') {
            ElMessage({
              type: 'warning',
              message: 'Registration cannot be completed unless you agree to the disclaimer.',
              showClose: true,
            })
          }
          return
        }
      }

      this.$emit('save', {
        firstName: this.form.firstName.trim(),
        lastName: this.form.lastName.trim(),
        email: this.form.email.trim(),
        primaryContactNumber: this.form.primaryContactNumber.trim(),
        isInternationalStudent: this.form.isInternationalStudent,
        countryOfCitizenship: this.form.countryOfCitizenship.trim(),
        currentStatus: this.form.currentStatus,
        currentStatusOther: this.form.currentStatus === 'other' ? this.form.currentStatusOther.trim() : '',
        highestEducation: this.form.highestEducation,
        highestEducationOther: this.form.highestEducation === 'other' ? this.form.highestEducationOther.trim() : '',
        requestedServices: [...this.form.requestedServices],
        preferredSchedule: this.form.preferredSchedule,
        specialRequirementsOrPreferences: this.form.specialRequirementsOrPreferences.trim(),
        serviceTimeFrame: this.form.serviceTimeFrame,
        registrationDecision: this.form.registrationDecision,
        completed: true,
      })
    }
  }
}
</script>

<style scoped>
.dialog-shell {
  display: grid;
  grid-template-columns: minmax(240px, 290px) minmax(0, 1fr);
  min-height: min(82vh, 860px);
  background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
  border-radius: 28px;
  overflow: hidden;
}

.dialog-hero {
  padding: 30px 24px;
  background:
    radial-gradient(circle at top, rgba(11, 108, 255, 0.2), transparent 42%),
    linear-gradient(180deg, #003831 0%, #05564d 100%);
  color: #f8fffd;
}

.hero-eyebrow {
  margin: 0 0 8px;
  color: #9fd0ff;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.dialog-hero h2 {
  margin: 0 0 10px;
  font-size: 2rem;
  line-height: 1.08;
  font-weight: 850;
}

.hero-copy {
  margin: 0 0 22px;
  color: rgba(248, 255, 253, 0.86);
}

.progress-card {
  margin-bottom: 22px;
  padding: 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.progress-label {
  display: block;
  margin-bottom: 6px;
  color: rgba(248, 255, 253, 0.76);
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.progress-card strong {
  display: block;
  margin-bottom: 12px;
  font-size: 1.35rem;
}

.dialog-main {
  display: flex;
  flex-direction: column;
  padding: 28px 28px 24px;
}

.profile-form {
  flex: 1 1 auto;
  overflow-y: auto;
  padding-right: 6px;
}

.step-panel {
  padding-bottom: 12px;
}

.section-card {
  padding: 20px;
  border-radius: 22px;
  background: #ffffff;
  border: 1px solid rgba(3, 36, 31, 0.08);
  box-shadow: 0 16px 36px rgba(3, 36, 31, 0.06);
}

.grid {
  display: grid;
  gap: 14px;
}

.two-up {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.option-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 18px;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.service-option {
  margin: 0;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(3, 36, 31, 0.1);
  background: linear-gradient(180deg, #ffffff 0%, #f7fbff 100%);
}

.option-group :deep(.el-radio) {
  margin-right: 0;
}

.option-grid :deep(.el-radio) {
  display: flex;
  align-items: center;
  min-height: 28px;
}

:deep(.el-form-item__label) {
  font-size: 18px;
  line-height: 1.45;
  font-weight: 700;
  color: #03241f;
}

.disclaimer-box {
  padding: 20px;
  border-radius: 20px;
  background: linear-gradient(180deg, #f7fbff 0%, #f3f8ff 100%);
  border: 1px solid rgba(11, 108, 255, 0.12);
  margin-bottom: 20px;
}

.disclaimer-title {
  margin: 0 0 10px;
  color: #03241f;
  font-size: 1rem;
  font-weight: 800;
}

.disclaimer-box > p {
  margin: 0 0 12px;
  color: #486a64;
}

.disclaimer-list {
  margin: 0;
  padding-left: 18px;
  color: #486a64;
}

.disclaimer-list li + li {
  margin-top: 10px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
}

:deep(.customer-profile-dialog .el-dialog) {
  max-width: calc(100vw - 24px);
  border-radius: 28px;
  overflow: hidden;
  background: transparent;
}

:deep(.customer-profile-dialog .el-dialog__header) {
  display: none;
}

:deep(.customer-profile-dialog .el-dialog__body) {
  padding: 0;
}

@media (max-width: 900px) {
  .dialog-shell {
    grid-template-columns: 1fr;
  }

  .dialog-hero {
    padding-bottom: 18px;
  }
}

@media (max-width: 680px) {
  .dialog-main {
    padding: 18px;
  }

  .two-up,
  .services-grid,
  .option-grid {
    grid-template-columns: 1fr;
  }

  .dialog-hero h2 {
    font-size: 1.6rem;
  }

}
</style>
